// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ONE_MINUTE_MS, TEN_MINUTES_MS } from '@/utilities/time-constants';
import { getWorkflows } from '@/workflow/api/workflows';
import { useQuery } from '@tanstack/react-query';

interface UseWorkflowsOptions {
  withCount?: boolean;
}

/**
 * Creates a normalized options object with defaults applied
 */
function normalizeOptions(
  options: UseWorkflowsOptions
): Required<UseWorkflowsOptions> {
  return {
    withCount: options.withCount ?? false,
  };
}

/**
 * Creates a stable query key for React Query caching
 */
function createWorkflowsQueryKey(options: Required<UseWorkflowsOptions>) {
  return ['workflows', options] as const;
}

/**
 * Hook to fetch and cache workflows for the current organization
 */
export function useWorkflows(options: UseWorkflowsOptions = {}) {
  const normalizedOptions = normalizeOptions(options);

  return useQuery({
    queryKey: createWorkflowsQueryKey(normalizedOptions),
    queryFn: async () => {
      const workflows = await getWorkflows(normalizedOptions.withCount);
      return workflows.sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 0, // This isn't that efficient, but it's fine for now, its important this is never stale
    gcTime: TEN_MINUTES_MS,
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchInterval: ONE_MINUTE_MS,
  });
}
